import { BNMaterialUI, KindlyChat, NobaChatLoginPrompt } from "@bn-monorepo/js-packages";
import React, { ReactNode, useContext, useEffect } from "react";

import CmsContentContainer from "./components/CmsContent/CmsContentContainer";

import { ChatProvider } from "@bn-monorepo/js-packages/src/Components/NobaChat";
import { useLocation } from "react-router";
import { InitialPageData } from "./client";
import { SettingsContext } from "./settingsContext";

interface Props {
  initialPageData?: InitialPageData;
}

const App: React.FC<Props> = ({ initialPageData }) => {
  const settings = useContext(SettingsContext);
  const {
    cookieConsentEnabled,
    cookieConsentHasConsented,
    cookieConsentWantsAnalysis,
    cookieConsentWantsMarketing,
    cookieConsentWantsPersonalization,
    kindleySettings,
    isApp,
    isAuthenticated,
    showNobaAssistant,
    showNobaAssistantDevTools,
    allowDarkMode,
    region,
    localization,
  } = settings;

  useEffect(() => {
    if (cookieConsentEnabled) {
      window.dataLayer?.push({
        CookieConsent: {
          HasConsented: cookieConsentHasConsented,
          WantsAnalysis: cookieConsentWantsAnalysis,
          WantsMarketing: cookieConsentWantsMarketing,
          WantsPersonalization: cookieConsentWantsPersonalization,
        },
      });
    }
  });

  const location = useLocation();
  const enableChatOnThisPath = shouldEnableChat(location.pathname.toLowerCase());

  const showNobaLoginPrompt = !kindleySettings && showNobaAssistant && !isAuthenticated && enableChatOnThisPath;

  let alternativeChat: ReactNode = undefined;
  if (kindleySettings) {
    alternativeChat = (
      <KindlyChat
        kindlyKey={kindleySettings.kindleyKey ?? ""}
        activePathList={kindleySettings.kindleyActivePathList ?? []}
      />
    );
  } else if (showNobaLoginPrompt) {
    alternativeChat = (
      <NobaChatLoginPrompt
        loginLink="/login/"
        text={localization.customerServiceChat_loginPromptText}
        linkLabel={localization.customerServiceChat_loginPromptLinkLabel}
      />
    );
  }

  return (
    <BNMaterialUI
      htmlFontSize={10}
      cssBaseLineEnabled
      emotionCacheKeySuffix={initialPageData?.pageContent?.contentId}
      isApp={isApp}
      darkThemeEnabled={allowDarkMode}
    >
      <ChatProvider
        disabled={!showNobaAssistant || !isAuthenticated || !enableChatOnThisPath}
        disableAlternative={!enableChatOnThisPath}
        regionId={region}
        alternativeChat={alternativeChat}
        alternativeSendMessage={(content) => {
          window.kindlyChat?.sendMessage(content, {
            sender: "user",
            openChat: true,
          });
        }}
        allowDevTools={showNobaAssistantDevTools}
      >
        <CmsContentContainer
          header={initialPageData?.header}
          cmsContent={initialPageData?.pageContent}
          footer={initialPageData?.footer}
          isLoggedIn={false}
        />
      </ChatProvider>
    </BNMaterialUI>
  );
};

function shouldEnableChat(path: string) {
  const customerServicePaths = [
    "/kundeservice/",
    "/kundservice/",
    "/sv/kundservice/",
    "/asiakaspalvelu/",
    "/kundenservice/",
    "/atencion-al-cliente/",
  ];
  if (customerServicePaths.some((p) => path.startsWith(p))) return true;

  return false;
}

export default App;
